function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import { ClickAwayListener, Fade, IconButton } from "@mui/material";
import { useIntersectionObserver, useToggle } from "@react-hookz/web";
import React, { useEffect, useMemo, useRef } from "react";
import { useSdkContext } from "~/components";
import { theme, useResponsive } from "~/lib/media";
import { DashboardBadge } from "./badge";
import { usePushNotification } from "./display/notification-panel/hooks";
import { useRefreshSubscription } from "./display/notification-panel/notification-setting/hooks";
import { useReadingHistorySetup } from "./display/reading-history-panel/hooks";
import { useIsFirstTimeVisitDashboardWidget } from "./hooks";
var CloseIcon = function CloseIcon(props) {
  return /*#__PURE__*/React.createElement("svg", props, /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M11.016 10.04 20 18.983 19.063 20 10 10.976 1.016 20 0 18.984l8.984-8.945L0 1.016 1.016 0 10 9.024 19.063 0 20 1.016l-8.984 9.023Z",
    fill: "#001246"
  }));
};
CloseIcon.defaultProps = {
  width: "20",
  height: "20",
  viewBox: "0 0 20 20",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
};
var LogoIcon = function LogoIcon(props) {
  return /*#__PURE__*/React.createElement("svg", props, /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M0 25h32V0H0v25Z",
    fill: "#FEFEFE"
  }), /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M2.76 22.2h11.854V2.797H2.76v19.405Z",
    fill: "#FFCA05"
  }), /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M17.387 22.2H29.24V2.797H17.387v19.405Z",
    fill: "#001246"
  }));
};
LogoIcon.defaultProps = {
  width: "32",
  height: "25",
  viewBox: "0 0 32 25",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
};
import { Container, DisplayDrawer, DisplayPopper, DrawerHeader, MenuContainer, StyledDashboardDisplay } from "./styles";
import { DashboardTooltips } from "./tooltips";
export const DashboardWidget = ({
  children,
  className,
  messageRenders,
  tooltipRenders
}) => {
  const {
    hasAcceptedGDPR,
    notification
  } = useSdkContext();
  useReadingHistorySetup();
  usePushNotification(notification.firebaseApp);
  useRefreshSubscription();
  const anchorElement = useRef(null);
  const [isOpen, toggleIsOpen] = useToggle(false);
  const [isTooltipVisible, setIsTooltipVisible] = useToggle(true);
  const observer = useIntersectionObserver(anchorElement);
  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);
  const isDesktop = useResponsive(theme.breakpoints.up("desktop"), false);
  const renderDisplay = () => /*#__PURE__*/React.createElement(StyledDashboardDisplay, {
    beforeHeaderSlot: /*#__PURE__*/React.createElement(DrawerHeader, null, /*#__PURE__*/React.createElement(LogoIcon, null), /*#__PURE__*/React.createElement(IconButton, {
      "aria-label": "close",
      disableRipple: true,
      onClick: toggleIsOpen
    }, /*#__PURE__*/React.createElement(CloseIcon, null))),
    messageRenders: messageRenders,
    "data-qa": "render-display"
  });
  const {
    setIsFirstTime
  } = useIsFirstTimeVisitDashboardWidget();
  useEffect(() => {
    if (!isOpen || !hasAcceptedGDPR) return;
    setIsFirstTime(false);
  }, [hasAcceptedGDPR, isOpen, setIsFirstTime]);
  useEffect(() => {
    setIsTooltipVisible(observer === null || observer === void 0 ? void 0 : observer.isIntersecting);
  }, [observer === null || observer === void 0 ? void 0 : observer.isIntersecting, setIsTooltipVisible]);
  const popperModifiers = useMemo(() => [{
    name: "offset",
    options: {
      // This is for handling tablet placement of the popper
      offset: [isDesktop ? 0 : 42, 10]
    }
  }], [isDesktop]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DashboardBadge, null, /*#__PURE__*/React.createElement(DashboardTooltips, {
    isDashboardOpen: isOpen,
    isTooltipVisible: isTooltipVisible,
    tooltipRenders: tooltipRenders
  }, /*#__PURE__*/React.createElement(Container, {
    className: className,
    ref: anchorElement,
    onClick: toggleIsOpen
  }, children))), /*#__PURE__*/React.createElement(DisplayDrawer, {
    anchor: "bottom",
    open: isMobile && isOpen,
    SlideProps: {
      easing: {
        enter: "ease"
      },
      timeout: {
        enter: 500,
        exit: 0
      }
    },
    hideBackdrop: true,
    onClose: () => toggleIsOpen()
  }, renderDisplay()), /*#__PURE__*/React.createElement(DisplayPopper, {
    anchorEl: anchorElement.current,
    modifiers: popperModifiers,
    open: !isMobile && isOpen,
    placement: "bottom-end",
    transition: true
  }, ({
    TransitionProps
  }) => /*#__PURE__*/React.createElement(ClickAwayListener, {
    onClickAway: () => toggleIsOpen(false)
  }, /*#__PURE__*/React.createElement(Fade, _extends({}, TransitionProps, {
    timeout: {
      enter: 500,
      exit: 0
    }
  }), /*#__PURE__*/React.createElement(MenuContainer, null, renderDisplay())))));
};